import React, { useEffect } from "react";
import {
  HStack,
  useColorModeValue as mode,
  VStack,
  Avatar,
  Text,
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Input,
  Button,
  Center,
} from "@chakra-ui/react";
import toast from "react-hot-toast";
import { getToken } from "firebaseClient";

// Utils
import { updateUser } from "utils/sessionHelper";
import { fetchMentionsForProfile } from "controllers/mentions";

// Styles
import { layerStyle, textStyle } from "theme/names";

// Types
import { Profile } from "types/mentions";

const baseUrl = "https://us-central1-ez4cast.cloudfunctions.net";

const AddProfileModal = ({ isOpen, onClose, onModalClose, session, account, onAdding }: { isOpen: boolean; onClose: () => void; onModalClose: () => void; session: any; account: any; onAdding: (username: string) => void; }) => {
  const [profileLink, setProfileLink] = React.useState<string>("");
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isAdding, setIsAdding] = React.useState<boolean>(false);
  const handleFindProfile = async () => {
    if (!profileLink) {
      toast.error("Please enter a profile link");
      return;
    }
    // Validate profile link format for LinkedIn
    const linkedinRegex = /https:\/\/www.linkedin.com\/(in|company)\/[a-zA-Z0-9-]+/;
    if (!linkedinRegex.test(profileLink)) {
      toast.error("Please enter a valid LinkedIn profile link");
      return;
    }
    setIsLoading(true);
    const token = await getToken(session, "Mentions-AddProfileModal");
    const profileRes = await fetch(
      `${baseUrl}/linkedinSocialListening-getAccountOrCompany`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          tokenUserId: session?.user.uid || "",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          accountId: account.id,
          profileLink,
          "isInfluentLeader": true,
        }),
      }
    );
    if (!profileRes.ok) {
      setIsLoading(false);
      toast.error("Something went wrong while retrieving profile");
      return;
    }
    const profileData = await profileRes.json();
    if (profileData.success === 0) {
      setIsLoading(false);
      toast.error(profileData.error || "Something went wrong while retrieving profile");
      return;
    }
    if (!profileData) {
      setIsLoading(false);
      toast.error("Profile not found");
      return;
    }
    const { profile } = profileData;
    setProfile({
      id: profile.id,
      dashEntityUrn: profile.dashEntityUrn,
      name: `${profile.firstName} ${profile.lastName ? profile.lastName : ""}`,
      username: profile.username,
      image: profile.imageHosted || profile.image,
      type: profile.type,
      isInitFetching: true,
    });
    setIsLoading(false);
  }
  const handleAddToMentions = async () => {
    if (!profile) {
      toast.error("Something went wrong while adding profile to mentions, please try again");
      return;
    }
    setIsAdding(true);
    try {
      // Update the user's mentions profiles
      await updateUser(session, { mentionsProfiles: account.mentionsProfiles ? [...account.mentionsProfiles, profile] : [profile] });

      // Fetch mentions for the newly added profile
      const token = await getToken(session, "Mentions-AddProfileModal");
      await fetchMentionsForProfile(account.idParent, profile, session);

      toast.success(`${profile.name} added to @mentions`);
      setProfileLink("");
      setProfile(null);
      onAdding(profile.username);
    }
    catch (e) {
      console.error(e);
      toast.error("Something went wrong while adding profile to mentions, please try again");
    }
    onModalClose();
    setIsAdding(false);
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add an account or company to @mentions</ModalHeader>
        <ModalCloseButton />
        <ModalBody border="none">
          <Text textStyle={textStyle.body.medium.light} mb={7}>Add any personal or company account to start monitoring their @mentions on LinkedIn.</Text>
          <HStack spacing={4} mb={7}>
            <Input placeholder="https://www.linkedin.com/company/taplio"
              value={profileLink}
              onChange={(e) => setProfileLink(e.target.value)}
            />
            <Button
              variant="primary"
              onClick={handleFindProfile}
              isLoading={isLoading}

            >Find</Button>
          </HStack>
          {profile && <VStack spacing={2} mb={6}>
            <Text textStyle={textStyle.small.bold} mb={2}>Success!</Text>
            <HStack spacing={2} layerStyle={layerStyle.bgBorderGrayRounded} px={5} py={3}>
              <Avatar name={profile.name} src={profile.image} size="xs" />
              <Text textStyle={textStyle.small.bold}>{profile.name}</Text>
            </HStack>
          </VStack>}
          <Center>
            <Button
              variant="primary"
              isDisabled={!profile}
              onClick={handleAddToMentions}
              isLoading={isAdding}
              loadingText={profile ? `Fetching @mentions of ${profile.name}` : undefined}
            >
              Add {profile ? profile.name : ""} to @mentions
            </Button>
          </Center>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default AddProfileModal;